<div class="card-view-box iot-intel-wrapper">
    <mat-card>
        <!-- ── Card Header ── -->
        <mat-card-header class="iot-card-header">
            <div class="iot-header-icon">
                <mat-icon>memory</mat-icon>
            </div>
            <div class="iot-header-info">
                <div class="iot-header-title">
                    <span class="iot-title-txt">IoT Gateway Intelligence</span>
                    <span
                        class="iot-status-dot"
                        [ngClass]="deviceStatus ? 'dot-online' : 'dot-offline'"
                    ></span>
                    <mat-chip-listbox class="iot-status-chip-list">
                        <mat-chip
                            class="iot-status-chip"
                            [ngClass]="deviceStatus ? 'success-bg-color-opacity' : 'error-bg-color-opacity'"
                        >
                            {{ deviceStatus ? 'ONLINE' : 'OFFLINE' }}
                        </mat-chip>
                    </mat-chip-listbox>
                </div>
                <div class="iot-header-sub">
                    <span class="iot-device-name">{{ deviceName || 'Loading…' }}</span>
                    <span class="iot-separator">·</span>
                    <span class="iot-device-id">{{ deviceId || '—' }}</span>
                </div>
            </div>
            @if (lastReadTime) {
                <div class="iot-header-lastread">
                    <span class="iot-lastread-label">Last read</span>
                    <span class="iot-lastread-val">{{ lastReadTime | date: 'dd MMM, HH:mm' }}</span>
                </div>
            }
        </mat-card-header>

        <mat-card-content class="iot-card-content">
            <!-- ── Controls Row ── -->
            <div class="iot-controls-row">
                <div class="iot-range-btns">
                    <button
                        class="iot-range-btn"
                        [ngClass]="{ 'iot-range-btn-active': activeRange === 'live' }"
                        (click)="setRange('live')"
                    >
                        Live
                    </button>
                    <button
                        class="iot-range-btn"
                        [ngClass]="{ 'iot-range-btn-active': activeRange === '6h' }"
                        (click)="setRange('6h')"
                    >
                        6H
                    </button>
                    <button
                        class="iot-range-btn"
                        [ngClass]="{ 'iot-range-btn-active': activeRange === '24h' }"
                        (click)="setRange('24h')"
                    >
                        24H
                    </button>
                    <button
                        class="iot-range-btn"
                        [ngClass]="{ 'iot-range-btn-active': activeRange === '7d' }"
                        (click)="setRange('7d')"
                    >
                        7D
                    </button>
                </div>
                <span class="iot-range-label">{{ rangeLabel }}</span>
                <div class="iot-controls-actions">
                    <button class="iot-refresh-btn" (click)="loadData()" [disabled]="isLoading">
                        <mat-icon class="iot-refresh-icon">refresh</mat-icon> Refresh
                    </button>
                </div>
            </div>

            <!-- ── Loading State ── -->
            @if (isLoading) {
                <div class="iot-skeleton-wrap">
                    <app-line-skeleton-loader [theme]="intelligenceCardTheme"></app-line-skeleton-loader>
                </div>
            } @else if (isError) {
                <!-- ── Error State ── -->
                <div class="iot-error-state">
                    <mat-icon class="iot-error-icon">error_outline</mat-icon>
                    <p class="iot-error-txt">Unable to load device intelligence data.</p>
                    <button class="iot-retry-btn" (click)="loadData()">
                        <mat-icon>refresh</mat-icon> Retry
                    </button>
                </div>
            } @else {
                <!-- ── KPI Strip ── -->
                <div class="iot-kpi-strip">
                    <!-- Health Ring -->
                    <div class="iot-health-ring-box">
                        <svg width="64" height="64" viewBox="0 0 64 64" overflow="visible">
                            <circle cx="32" cy="32" r="26" fill="none" stroke="#E0E0E0" stroke-width="8" />
                            <circle
                                cx="32"
                                cy="32"
                                r="26"
                                fill="none"
                                [attr.stroke]="healthRingColor"
                                stroke-width="8"
                                stroke-dasharray="163.4 163.4"
                                [attr.stroke-dashoffset]="healthRingOffset"
                                stroke-linecap="round"
                                transform="rotate(-90 32 32)"
                                style="transition: stroke-dashoffset 1s ease"
                            />
                            <text
                                x="32"
                                y="37"
                                text-anchor="middle"
                                font-size="11"
                                font-weight="700"
                                font-family="IBM Plex Sans, sans-serif"
                                [attr.fill]="healthRingColor"
                            >
                                {{ analytics.healthScore }}%
                            </text>
                        </svg>
                        <p class="iot-ring-label">Health</p>
                    </div>

                    <!-- KPI Chips Grid -->
                    <div class="iot-kpi-chips">
                        <div class="iot-kpi-chip">
                            <div class="kpi-val kpi-blue">{{ analytics.availability }}%</div>
                            <div class="kpi-lbl">Avail.</div>
                        </div>
                        <div class="iot-kpi-chip">
                            <div
                                class="kpi-val"
                                [ngClass]="analytics.alertCount > 0 ? 'kpi-red' : 'kpi-green'"
                            >
                                {{ analytics.alertCount }}
                            </div>
                            <div class="kpi-lbl">Alerts</div>
                        </div>
                        <div class="iot-kpi-chip">
                            <div class="kpi-val kpi-purple">{{ analytics.paramCount }}</div>
                            <div class="kpi-lbl">Params</div>
                        </div>
                        @if (analytics.power.hasPowerData) {
                            <div class="iot-kpi-chip">
                                <div class="kpi-val kpi-amber">
                                    {{
                                        analytics.power.kw !== null
                                            ? (analytics.power.kw | number: '1.1-1')
                                            : '—'
                                    }}
                                </div>
                                <div class="kpi-lbl">Act. kW</div>
                            </div>
                            <div class="iot-kpi-chip">
                                <div class="kpi-val" [ngClass]="pfQualityClass">
                                    {{
                                        analytics.power.pfAvg !== null
                                            ? (analytics.power.pfAvg | number: '1.2-2')
                                            : '—'
                                    }}
                                </div>
                                <div class="kpi-lbl">Pwr Factor</div>
                            </div>
                            <div class="iot-kpi-chip">
                                <div class="kpi-val kpi-cyan">
                                    {{
                                        analytics.power.energyEfficiency !== null
                                            ? (analytics.power.energyEfficiency | number: '1.0-0') + '%'
                                            : '—'
                                    }}
                                </div>
                                <div class="kpi-lbl">Energy Eff.</div>
                            </div>
                        }
                    </div>
                </div>

                <!-- ── Tab Navigation ── -->
                <div class="iot-tab-nav">
                    <button
                        class="iot-tab-btn"
                        [ngClass]="{ 'iot-tab-btn-active': activeTab === 'overview' }"
                        (click)="activeTab = 'overview'"
                    >
                        <mat-icon class="iot-tab-icon">grid_view</mat-icon>
                        Overview
                    </button>
                    <button
                        class="iot-tab-btn"
                        [ngClass]="{ 'iot-tab-btn-active': activeTab === 'power' }"
                        (click)="activeTab = 'power'"
                    >
                        <mat-icon class="iot-tab-icon">bolt</mat-icon>
                        Power Analytics
                    </button>
                    <button
                        class="iot-tab-btn"
                        [ngClass]="{ 'iot-tab-btn-active': activeTab === 'maintenance' }"
                        (click)="activeTab = 'maintenance'"
                    >
                        <mat-icon class="iot-tab-icon">build</mat-icon>
                        Maintenance
                    </button>
                </div>

                <!-- ── Overview Tab ── -->
                <div [hidden]="activeTab !== 'overview'" class="iot-tab-content">
                    @if (parameters.length === 0) {
                        <div class="iot-empty-state">
                            <mat-icon class="iot-empty-icon">sensors_off</mat-icon>
                            <p class="iot-empty-txt">No parameter data available for this device.</p>
                        </div>
                    } @else {
                        <div class="row iot-param-grid">
                            @for (param of parameters; track param.parameterId || $index) {
                                <div class="col-12 col-sm-6 col-xl-4 iot-param-col">
                                    <div
                                        class="iot-param-card"
                                        [ngClass]="param.inRange ? 'param-ok' : 'param-alert'"
                                    >
                                        <!-- Param header -->
                                        <div
                                            class="iot-param-name-row"
                                            [ngClass]="param.inRange ? 'param-name-ok' : 'param-name-alert'"
                                        >
                                            <span class="iot-param-name-txt">{{ param.parameterName }}</span>
                                            <span
                                                class="iot-trend-arrow"
                                                [ngClass]="{
                                                    'trend-up': param.trend === 'up',
                                                    'trend-down': param.trend === 'down',
                                                    'trend-stable': param.trend === 'stable'
                                                }"
                                                [title]="param.trend"
                                            >
                                                @if (param.trend === 'up') {
                                                    <mat-icon class="trend-icon">trending_up</mat-icon>
                                                } @else if (param.trend === 'down') {
                                                    <mat-icon class="trend-icon">trending_down</mat-icon>
                                                } @else {
                                                    <mat-icon class="trend-icon">trending_flat</mat-icon>
                                                }
                                            </span>
                                        </div>

                                        <!-- Current value -->
                                        <div class="iot-param-value-row">
                                            <span class="iot-param-current-val">{{ param.lastValue || '—' }}</span>
                                            <span class="iot-param-uom">{{ param.uom }}</span>
                                        </div>

                                        <!-- Progress bar -->
                                        <div class="iot-param-bar-wrap">
                                            <div class="iot-param-bar-track">
                                                <div
                                                    class="iot-param-bar-fill"
                                                    [ngClass]="param.inRange ? 'bar-ok' : 'bar-alert'"
                                                    [style.width.%]="getParamBarWidth(param)"
                                                ></div>
                                                <div
                                                    class="iot-param-bar-avg-marker"
                                                    [style.left.%]="getAvgMarkerPosition(param)"
                                                    title="Average"
                                                ></div>
                                            </div>
                                        </div>

                                        <!-- Min / Avg / Max -->
                                        <div class="iot-param-stats-row">
                                            <div class="iot-stat">
                                                <span class="iot-stat-lbl">MIN</span>
                                                <span class="iot-stat-val">{{ param.min }}</span>
                                            </div>
                                            <div class="iot-stat iot-stat-center">
                                                <span class="iot-stat-lbl">AVG</span>
                                                <span class="iot-stat-val">{{ param.avg }}</span>
                                            </div>
                                            <div class="iot-stat iot-stat-right">
                                                <span class="iot-stat-lbl">MAX</span>
                                                <span class="iot-stat-val">{{ param.max }}</span>
                                            </div>
                                        </div>

                                        <!-- Diff vs avg -->
                                        @if (param.diffPct !== 0) {
                                            <div
                                                class="iot-param-diff"
                                                [ngClass]="param.diffPct > 0 ? 'diff-up' : 'diff-down'"
                                            >
                                                {{ param.diffPct > 0 ? '+' : ''
                                                }}{{ param.diffPct | number: '1.1-1' }}% vs avg
                                            </div>
                                        }
                                    </div>
                                </div>
                            }
                        </div>
                    }
                </div>

                <!-- ── Power Analytics Tab ── -->
                <div [hidden]="activeTab !== 'power'" class="iot-tab-content">
                    @if (!analytics.power.hasPowerData) {
                        <div class="iot-empty-state">
                            <mat-icon class="iot-empty-icon">power_off</mat-icon>
                            <p class="iot-empty-txt">No electrical parameters detected for this device type.</p>
                        </div>
                    } @else {
                        <!-- Electrical Measurements -->
                        @if (analytics.power.voltage !== null || analytics.power.current !== null || analytics.power.frequency !== null || analytics.power.load !== null) {
                            <div class="iot-power-section">
                                <h6 class="iot-section-title">
                                    <mat-icon class="iot-section-icon">electric_bolt</mat-icon>
                                    Electrical Measurements
                                </h6>
                                @if (analytics.power.voltage !== null) {
                                    <div class="iot-power-row">
                                        <span class="iot-power-lbl">Voltage (V)</span>
                                        <div class="iot-bar-track">
                                            <div class="iot-bar-fill iot-bar-blue" [style.width.%]="Math.min(100, (analytics.power.voltage / 480) * 100)"></div>
                                        </div>
                                        <span class="iot-power-val">{{ analytics.power.voltage | number: '1.1-1' }}</span>
                                    </div>
                                }
                                @if (analytics.power.current !== null) {
                                    <div class="iot-power-row">
                                        <span class="iot-power-lbl">Current (A)</span>
                                        <div class="iot-bar-track">
                                            <div class="iot-bar-fill iot-bar-amber" [style.width.%]="Math.min(100, (analytics.power.current / 100) * 100)"></div>
                                        </div>
                                        <span class="iot-power-val">{{ analytics.power.current | number: '1.1-1' }}</span>
                                    </div>
                                }
                                @if (analytics.power.frequency !== null) {
                                    <div class="iot-power-row">
                                        <span class="iot-power-lbl">Frequency (Hz)</span>
                                        <div class="iot-bar-track">
                                            <div class="iot-bar-fill iot-bar-green" [style.width.%]="Math.min(100, (analytics.power.frequency / 60) * 100)"></div>
                                        </div>
                                        <span class="iot-power-val">{{ analytics.power.frequency | number: '1.2-2' }}</span>
                                    </div>
                                }
                                @if (analytics.power.load !== null) {
                                    <div class="iot-power-row">
                                        <span class="iot-power-lbl">Load (%)</span>
                                        <div class="iot-bar-track">
                                            <div class="iot-bar-fill iot-bar-purple" [style.width.%]="Math.min(100, analytics.power.load)"></div>
                                        </div>
                                        <span class="iot-power-val">{{ analytics.power.load | number: '1.1-1' }}%</span>
                                    </div>
                                }
                            </div>
                        }

                        <!-- Power Triangle -->
                        @if (analytics.power.kw !== null || analytics.power.kvar !== null || analytics.power.pfAvg !== null) {
                            <div class="iot-power-section">
                                <h6 class="iot-section-title">
                                    <mat-icon class="iot-section-icon">electrical_services</mat-icon>
                                    Power Triangle
                                </h6>
                                @if (analytics.power.kw !== null) {
                                    <div class="iot-power-row">
                                        <span class="iot-power-lbl">Active Power (kW)</span>
                                        <div class="iot-bar-track">
                                            <div class="iot-bar-fill iot-bar-blue" [style.width.%]="getPowerBarWidth('kw')"></div>
                                        </div>
                                        <span class="iot-power-val">{{ analytics.power.kw | number: '1.1-1' }}</span>
                                    </div>
                                }
                                @if (analytics.power.kvar !== null) {
                                    <div class="iot-power-row">
                                        <span class="iot-power-lbl">Reactive Power (kVAR)</span>
                                        <div class="iot-bar-track">
                                            <div class="iot-bar-fill iot-bar-amber" [style.width.%]="getPowerBarWidth('kvar')"></div>
                                        </div>
                                        <span class="iot-power-val">{{ analytics.power.kvar | number: '1.1-1' }}</span>
                                    </div>
                                }
                                @if (analytics.power.pfAvg !== null) {
                                    <div class="iot-power-badges">
                                        <span class="iot-badge" [ngClass]="'iot-badge-' + pfQualityClass">
                                            PF {{ analytics.power.pfAvg | number: '1.2-2' }} — {{ analytics.power.pfQuality }}
                                        </span>
                                        @if (analytics.power.loadFactor !== null) {
                                            <span class="iot-badge iot-badge-gray">
                                                Load Factor: {{ analytics.power.loadFactor | number: '1.0-0' }}%
                                            </span>
                                        }
                                    </div>
                                }
                            </div>
                        }

                        <!-- Phase Balance -->
                        @if (analytics.power.pf1 !== null || analytics.power.pf2 !== null || analytics.power.pf3 !== null) {
                            <div class="iot-power-section">
                                <h6 class="iot-section-title">
                                    <mat-icon class="iot-section-icon">device_hub</mat-icon>
                                    Phase Balance
                                </h6>
                                @if (analytics.power.pf1 !== null) {
                                    <div class="iot-power-row">
                                        <span class="iot-power-lbl">Phase 1 PF</span>
                                        <div class="iot-bar-track">
                                            <div class="iot-bar-fill iot-bar-blue" [style.width.%]="(analytics.power.pf1 || 0) * 100"></div>
                                        </div>
                                        <span class="iot-power-val">{{ analytics.power.pf1 | number: '1.2-2' }}</span>
                                    </div>
                                }
                                @if (analytics.power.pf2 !== null) {
                                    <div class="iot-power-row">
                                        <span class="iot-power-lbl">Phase 2 PF</span>
                                        <div class="iot-bar-track">
                                            <div class="iot-bar-fill iot-bar-green" [style.width.%]="(analytics.power.pf2 || 0) * 100"></div>
                                        </div>
                                        <span class="iot-power-val">{{ analytics.power.pf2 | number: '1.2-2' }}</span>
                                    </div>
                                }
                                @if (analytics.power.pf3 !== null) {
                                    <div class="iot-power-row">
                                        <span class="iot-power-lbl">Phase 3 PF</span>
                                        <div class="iot-bar-track">
                                            <div class="iot-bar-fill iot-bar-amber" [style.width.%]="(analytics.power.pf3 || 0) * 100"></div>
                                        </div>
                                        <span class="iot-power-val">{{ analytics.power.pf3 | number: '1.2-2' }}</span>
                                    </div>
                                }
                                @if (analytics.power.phaseBalanceScore !== null) {
                                    <div class="iot-phase-score-row">
                                        Phase Balance Score:
                                        <strong>{{ analytics.power.phaseBalanceScore | number: '1.0-0' }}/100</strong>
                                    </div>
                                }
                            </div>
                        }

                        <!-- Energy Breakdown -->
                        @if (analytics.power.activeEnergy !== null || analytics.power.reactiveEnergy !== null || analytics.power.apparentEnergy !== null) {
                            <div class="iot-power-section">
                                <h6 class="iot-section-title">
                                    <mat-icon class="iot-section-icon">battery_charging_full</mat-icon>
                                    Energy Breakdown
                                </h6>
                                @if (analytics.power.activeEnergy !== null) {
                                    <div class="iot-power-row">
                                        <span class="iot-power-lbl">Active Energy (kWh)</span>
                                        <div class="iot-bar-track">
                                            <div class="iot-bar-fill iot-bar-blue" [style.width.%]="getEnergyBarWidth('active')"></div>
                                        </div>
                                        <span class="iot-power-val">{{ analytics.power.activeEnergy | number: '1.1-1' }}</span>
                                    </div>
                                }
                                @if (analytics.power.reactiveEnergy !== null) {
                                    <div class="iot-power-row">
                                        <span class="iot-power-lbl">Reactive Energy (kVARh)</span>
                                        <div class="iot-bar-track">
                                            <div class="iot-bar-fill iot-bar-amber" [style.width.%]="getEnergyBarWidth('reactive')"></div>
                                        </div>
                                        <span class="iot-power-val">{{ analytics.power.reactiveEnergy | number: '1.1-1' }}</span>
                                    </div>
                                }
                                @if (analytics.power.apparentEnergy !== null) {
                                    <div class="iot-power-row">
                                        <span class="iot-power-lbl">Apparent Energy (kVAh)</span>
                                        <div class="iot-bar-track">
                                            <div class="iot-bar-fill iot-bar-gray" [style.width.%]="getEnergyBarWidth('apparent')"></div>
                                        </div>
                                        <span class="iot-power-val">{{ analytics.power.apparentEnergy | number: '1.1-1' }}</span>
                                    </div>
                                }
                                <div class="iot-energy-badges">
                                    @if (analytics.power.energyEfficiency !== null) {
                                        <span class="iot-badge iot-badge-kpi-blue">
                                            Energy Efficiency: {{ analytics.power.energyEfficiency | number: '1.0-0' }}%
                                        </span>
                                    }
                                    @if (analytics.power.reactiveRatio !== null) {
                                        <span class="iot-badge iot-badge-gray">
                                            Reactive Ratio: {{ analytics.power.reactiveRatio | number: '1.0-0' }}%
                                        </span>
                                    }
                                </div>
                            </div>
                        }
                    }
                </div>

                <!-- ── Maintenance Tab ── -->
                <div [hidden]="activeTab !== 'maintenance'" class="iot-tab-content">
                    @if (analytics.maintenanceAlerts.length === 0) {
                        <div class="iot-maint-clear">
                            <mat-icon class="maint-clear-icon">check_circle</mat-icon>
                            <p class="maint-clear-txt">
                                All parameters within normal range — no maintenance alerts.
                            </p>
                        </div>
                    } @else {
                        <div class="iot-maint-alerts-list">
                            @for (alert of analytics.maintenanceAlerts; track alert.parameterId || $index) {
                                <div class="iot-maint-alert-row">
                                    <mat-icon class="maint-alert-icon">warning</mat-icon>
                                    <div class="maint-alert-info">
                                        <span class="maint-alert-name">{{ alert.parameterName }}</span>
                                        <span class="maint-alert-detail">
                                            Current: {{ alert.lastValue }} {{ alert.uom }} · Range:
                                            {{ alert.min }} – {{ alert.max }}
                                        </span>
                                    </div>
                                    <span
                                        class="maint-alert-diff"
                                        [ngClass]="alert.diffPct > 0 ? 'diff-up' : 'diff-down'"
                                    >
                                        {{ alert.diffPct > 0 ? '+' : ''
                                        }}{{ alert.diffPct | number: '1.1-1' }}%
                                    </span>
                                </div>
                            }
                        </div>
                    }

                    <!-- Maintenance Score -->
                    <div class="iot-maint-score-box">
                        <div class="maint-score-header">
                            <span class="maint-score-label">Maintenance Score</span>
                            <span class="maint-score-pct" [ngClass]="maintenanceScoreClass">
                                {{ analytics.maintenanceScore }}%
                            </span>
                        </div>
                        <div class="maint-score-bar-track">
                            <div
                                class="maint-score-bar-fill"
                                [ngClass]="maintenanceScoreClass"
                                [style.width.%]="analytics.maintenanceScore"
                            ></div>
                        </div>
                        <p class="maint-score-verdict">{{ maintenanceVerdict }}</p>
                    </div>

                    <!-- Trend Summary -->
                    <div class="iot-trend-summary">
                        @if (analytics.trendRising.length > 0) {
                            <div class="trend-summary-row trend-row-up">
                                <mat-icon class="trend-summary-icon">trending_up</mat-icon>
                                <span
                                    ><strong>Rising:</strong>
                                    {{ trendRisingNames }}</span
                                >
                            </div>
                        }
                        @if (analytics.trendFalling.length > 0) {
                            <div class="trend-summary-row trend-row-down">
                                <mat-icon class="trend-summary-icon">trending_down</mat-icon>
                                <span
                                    ><strong>Falling:</strong>
                                    {{ trendFallingNames }}</span
                                >
                            </div>
                        }
                        @if (analytics.trendStable.length > 0) {
                            <div class="trend-summary-row trend-row-stable">
                                <mat-icon class="trend-summary-icon">trending_flat</mat-icon>
                                <span
                                    ><strong>Stable:</strong>
                                    {{ trendStableNames }}</span
                                >
                            </div>
                        }
                    </div>
                </div>

                <!-- ── Card Footer ── -->
                <div class="iot-card-footer">
                    @if (fetchedAt) {
                        <span class="iot-footer-ts">
                            Fetched at {{ fetchedAt | date: 'HH:mm:ss' }}
                        </span>
                    }
                    <button class="iot-footer-refresh-btn" (click)="loadData()" [disabled]="isLoading">
                        <mat-icon class="iot-footer-icon">refresh</mat-icon> Force refresh
                    </button>
                </div>
            }
        </mat-card-content>
    </mat-card>
</div>
